(() => {
  'use strict';

  const configElement = document.getElementById('ThemeConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  let cartQueue = Promise.resolve();
  let pendingCartChanges = 0;
  let statusTimer;
  const productRequests = new WeakMap();
  const variantSnapshotCreatedAt = Date.now();
  const searchRequests = new WeakMap();
  const searchTimers = new WeakMap();

  function announce(message) {
    const status = document.getElementById('StoreStatus');
    if (!status) return;
    status.textContent = message;
    status.classList.add('show');
    clearTimeout(statusTimer);
    statusTimer = setTimeout(() => status.classList.remove('show'), 4500);
  }

  function errorMessage(error, fallback) {
    if (typeof error.description === 'string') return error.description;
    if (typeof error.message === 'string') return error.message;
    if (typeof error.errors === 'string') return error.errors;
    if (error.errors && typeof error.errors === 'object') return Object.values(error.errors).flat().join(' ');
    return fallback;
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...options.headers }
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(errorMessage(result, config.cartError));
    return result;
  }

  function formError(form, message) {
    const target = form?.querySelector('.form-error');
    if (target) {
      target.textContent = message || '';
      target.hidden = !message;
    } else if (message) announce(message);
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
  }

  function cartSectionIds() {
    const ids = [];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    const mainCart = document.querySelector('[data-main-cart]');
    if (mainCart) ids.push(mainCart.dataset.sectionId);
    return ids;
  }

  function replaceCartSections(sections = {}) {
    const parser = new DOMParser();
    for (const [id, markup] of Object.entries(sections)) {
      if (!markup) continue;
      const parsed = parser.parseFromString(markup, 'text/html');
      if (id === 'cart-drawer') {
        const content = document.getElementById('CartDrawerContent');
        const rendered = parsed.getElementById('shopify-section-cart-drawer');
        if (content && rendered) content.innerHTML = rendered.innerHTML;
      } else {
        const current = document.querySelector(`[data-main-cart][data-section-id="${CSS.escape(id)}"]`);
        const next = parsed.querySelector('[data-main-cart]');
        if (current && next) current.replaceWith(next);
      }
    }
  }

  async function refreshCart(result = {}) {
    const ids = cartSectionIds();
    const cart = Number.isFinite(result.item_count) ? result : await request(`${config.root}cart.js`);
    document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; });
    const missing = ids.filter((id) => !result.sections?.[id]);
    let additional = {};
    if (missing.length) {
      const url = new URL(location.href);
      url.searchParams.set('sections', missing.join(','));
      additional = await request(url.toString());
    }
    replaceCartSections({ ...result.sections, ...additional });
    return cart;
  }

  function enqueueCart(task) {
    pendingCartChanges += 1;
    const next = cartQueue.then(task, task);
    cartQueue = next.catch(() => {}).finally(() => { pendingCartChanges -= 1; });
    return next;
  }

  async function addProduct(form) {
    const button = form.querySelector('button[type="submit"]');
    if (button?.disabled) return;
    formError(form, '');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      await enqueueCart(async () => {
        const body = new FormData(form);
        const ids = cartSectionIds();
        if (ids.length) body.set('sections', ids.join(','));
        body.set('sections_url', location.pathname);
        const result = await request(`${config.cartAddUrl}.js`, { method: 'POST', body });
        await refreshCart(result);
      });
      if (config.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        openDialog(document.getElementById('CartDrawer'));
        announce(config.added);
      } else location.assign(config.cartUrl);
    } catch (error) { formError(form, error.message || config.addError); }
    finally {
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
    }
  }

  async function changeCart(line, quantity) {
    const region = line.closest('dialog') || document.querySelector('[data-main-cart]');
    const controls = region ? [...region.querySelectorAll('[data-cart-delta], [data-cart-quantity]')] : [];
    controls.forEach((element) => { element.disabled = true; });
    try {
      await enqueueCart(async () => {
        const body = { id: line.dataset.cartKey, quantity: Math.max(0, Math.floor(Number(quantity) || 0)), sections: cartSectionIds(), sections_url: location.pathname };
        const result = await request(`${config.cartChangeUrl}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        await refreshCart(result);
      });
    } catch (error) { announce(error.message || config.cartError); }
    finally { controls.forEach((element) => { element.disabled = false; }); }
  }

  async function saveCartNote(input) {
    try {
      await enqueueCart(() => request(`${config.cartUpdateUrl}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: input.value })
      }));
    } catch (error) { announce(error.message || config.cartError); }
  }

  function initializeModels(scope) {
    const models = scope.querySelectorAll('model-viewer');
    if (!models.length || !window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
      if (errors) return;
      models.forEach((model) => { if (!model.dataset.initialized && window.Shopify.ModelViewerUI) { new window.Shopify.ModelViewerUI(model); model.dataset.initialized = 'true'; } });
    } }]);
  }

  async function updateVariant(root, changedInput) {
    productRequests.get(root)?.abort();
    const controller = new AbortController();
    productRequests.set(root, controller);
    const optionIds = [...root.querySelectorAll('select[data-product-option], input[data-product-option]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((input) => input.value);
    const url = new URL(root.dataset.productUrl, location.origin);
    url.searchParams.set('section_id', root.dataset.sectionId);
    url.searchParams.set('option_values', optionIds.join(','));
    const selectedId = changedInput.value;
    const selectedPosition = changedInput.dataset.optionPosition;
    const button = root.querySelector('[data-product-submit]');
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    root.querySelectorAll('form[data-product-form] input[name="id"]').forEach((input) => {
      input.value = '';
      input.disabled = true;
    });
    root.querySelectorAll('form[data-product-form]').forEach((form) => { form.inert = true; });
    let finished = false;
    try {
      const localCache = Date.now() - variantSnapshotCreatedAt < 300000
        && !root.querySelector('[name="selling_plan"]')
        && root.querySelector('[data-variant-state-cache]');
      const cachedState = localCache
        ? localCache.querySelector(`template[data-variant-state="${CSS.escape(optionIds.join(','))}"]`)
          || localCache.querySelector('template[data-variant-unavailable]')
        : null;
      let next = cachedState?.content;
      if (!next) {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(config.addError);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        next = parsed.querySelector('[data-product-root]');
      }
      if (controller.signal.aborted || productRequests.get(root) !== controller) return;
      if (!next) throw new Error(config.addError);
      const variantNode = next.querySelector('[data-selected-variant]');
      const variant = variantNode ? JSON.parse(variantNode.textContent) : null;
      const oldPrice = root.querySelector('[data-product-price]');
      const newPrice = next.querySelector('[data-product-price]');
      if (oldPrice && newPrice) oldPrice.innerHTML = newPrice.innerHTML;
      const oldOptions = root.querySelector('[data-option-selectors]');
      const newOptions = next.querySelector('[data-option-selectors]');
      if (oldOptions && newOptions) oldOptions.replaceWith(newOptions);
      root.querySelectorAll('form[data-product-form] input[name="id"]').forEach((input) => {
        input.value = variant?.id || '';
        input.disabled = !variant;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const newButton = next.querySelector('[data-product-submit]');
      if (button && newButton) { button.innerHTML = newButton.innerHTML; button.disabled = newButton.disabled; }
      const currentQuantity = root.querySelector('input[name="quantity"]');
      const nextQuantity = next.querySelector('input[name="quantity"]');
      if (currentQuantity && nextQuantity) {
        for (const attribute of ['min', 'max', 'step']) {
          if (nextQuantity.hasAttribute(attribute)) currentQuantity.setAttribute(attribute, nextQuantity.getAttribute(attribute));
          else currentQuantity.removeAttribute(attribute);
        }
        const min = Number(currentQuantity.min) || 1;
        const step = Number(currentQuantity.step) || 1;
        let quantity = min + Math.ceil((Math.max(min, Number(currentQuantity.value) || min) - min) / step) * step;
        if (currentQuantity.hasAttribute('max')) {
          const max = Number(currentQuantity.max);
          quantity = Math.min(quantity, min + Math.floor((max - min) / step) * step);
        }
        currentQuantity.value = String(quantity);
      }
      root.querySelectorAll('form[data-product-form]').forEach((form) => { form.inert = !variant?.available; });
      const currentVariant = root.querySelector('[data-selected-variant]');
      if (currentVariant) currentVariant.textContent = JSON.stringify(variant);
      const browserUrl = new URL(location.href);
      if (variant) {
        browserUrl.searchParams.set('variant', variant.id);
        browserUrl.searchParams.delete('option_values');
        history.replaceState({}, '', browserUrl);
        const thumbnail = root.querySelector(`[data-media-id="${variant.featured_media?.id}"]`) || root.querySelector('[data-media-select]');
        if (thumbnail) showMedia(thumbnail);
      } else {
        browserUrl.searchParams.delete('variant');
        browserUrl.searchParams.set('option_values', optionIds.join(','));
        history.replaceState({}, '', browserUrl);
      }
      const restoredInput = root.querySelector(`select[data-product-option][data-option-position="${CSS.escape(selectedPosition)}"]`) || root.querySelector(`input[data-product-option][value="${CSS.escape(selectedId)}"]`);
      restoredInput?.focus({ preventScroll: true });
      finished = true;
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted && productRequests.get(root) === controller) {
        announce(error.message || config.addError);
        const fallbackUrl = new URL(root.dataset.productUrl, location.origin);
        fallbackUrl.searchParams.set('option_values', optionIds.join(','));
        location.assign(fallbackUrl);
      }
    } finally {
      if (productRequests.get(root) === controller) {
        if (button) button.removeAttribute('aria-busy');
        if (!finished && !controller.signal.aborted && button) button.disabled = true;
      }
    }
  }

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-product-root]')) location.reload();
  });

  function showMedia(button) {
    const template = document.getElementById(button.dataset.mediaSelect);
    const root = button.closest('[data-product-root]');
    const target = root?.querySelector('[data-main-media]');
    if (!template || !target) return;
    target.querySelectorAll('video').forEach((video) => video.pause());
    target.replaceChildren(template.content.cloneNode(true));
    root.querySelectorAll('[data-media-select]').forEach((element) => element.setAttribute('aria-pressed', String(element === button)));
    initializeModels(target);
  }

  function closePredictiveSearch(container) {
    const results = container.querySelector('[data-predictive-results]');
    if (results) results.hidden = true;
    const input = container.querySelector('input[type="search"]');
    input?.setAttribute('aria-expanded', 'false');
    input?.removeAttribute('aria-activedescendant');
  }

  async function predictiveSearch(container, term) {
    searchRequests.get(container)?.abort();
    if (term.length < 2) { closePredictiveSearch(container); return; }
    const controller = new AbortController();
    searchRequests.set(container, controller);
    const url = new URL(config.predictiveUrl, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[type]', config.searchArticles ? 'product,article,page' : 'product');
    url.searchParams.set('resources[limit]', '6');
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Search unavailable');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = parsed.querySelector('#shopify-section-predictive-search');
      const results = container.querySelector('[data-predictive-results]');
      if (!content || !results) return;
      results.innerHTML = content.innerHTML;
      const options = results.querySelectorAll('[role="option"]');
      options.forEach((option, index) => { option.id = `${results.id}-option-${index}`; });
      results.hidden = !options.length;
      container.querySelector('input[type="search"]')?.setAttribute('aria-expanded', String(!!options.length));
    } catch (error) { if (error.name !== 'AbortError') closePredictiveSearch(container); }
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (form.matches('form[data-product-form]')) {
      event.preventDefault();
      await addProduct(form);
    } else if (event.submitter?.name === 'checkout' && pendingCartChanges > 0 && !form.dataset.readyForCheckout) {
      event.preventDefault();
      const submitter = event.submitter;
      await cartQueue;
      const currentForm = (form.id && document.getElementById(form.id)) || form;
      const currentSubmitter = currentForm === form ? submitter : currentForm.querySelector('[name="checkout"]');
      currentForm.dataset.readyForCheckout = 'true';
      if (currentSubmitter) currentForm.requestSubmit(currentSubmitter);
      else currentForm.requestSubmit();
    }
  });

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('[data-cart-open]');
    if (cartLink && document.getElementById('CartDrawer')) {
      event.preventDefault();
      openDialog(document.getElementById('CartDrawer'));
      return;
    }
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) openDialog(document.getElementById(opener.dataset.dialogOpen));
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeCart(remove.closest('[data-cart-line]'), 0); }
    const cartDelta = event.target.closest('[data-cart-delta]');
    if (cartDelta) {
      const line = cartDelta.closest('[data-cart-line]');
      const input = line.querySelector('[data-cart-quantity]');
      changeCart(line, Number(input.value) + Number(cartDelta.dataset.cartDelta));
    }
    const productDelta = event.target.closest('[data-product-delta]');
    if (productDelta) {
      const input = productDelta.closest('.quantity').querySelector('input');
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      input.value = String(Math.min(max, Math.max(min, (Number(input.value) || min) + Number(productDelta.dataset.productDelta) * step)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const media = event.target.closest('[data-media-select]');
    if (media) showMedia(media);
    const announcement = event.target.closest('[data-announcement-step]');
    if (announcement) {
      const region = announcement.closest('.announcement');
      const slides = [...region.querySelectorAll('[data-announcement-slide]')];
      const current = Math.max(0, slides.findIndex((slide) => !slide.hidden));
      const next = (current + Number(announcement.dataset.announcementStep) + slides.length) % slides.length;
      slides.forEach((slide, index) => { slide.hidden = index !== next; });
    }
    const predictive = event.target.closest('[data-predictive-search]');
    if (!predictive) document.querySelectorAll('[data-predictive-search]').forEach(closePredictiveSearch);
    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input.matches('[data-cart-quantity]')) changeCart(input.closest('[data-cart-line]'), input.value);
    if (input.matches('[data-cart-note]')) saveCartNote(input);
    if (input.matches('[data-localization-select], [data-auto-submit]')) input.form?.requestSubmit();
    if (input.matches('[data-product-option]')) updateVariant(input.closest('[data-product-root]'), input);
  });

  document.addEventListener('input', (event) => {
    const container = event.target.closest('[data-predictive-search]');
    if (!container || event.target.type !== 'search') return;
    clearTimeout(searchTimers.get(container));
    searchTimers.set(container, setTimeout(() => predictiveSearch(container, event.target.value.trim()), 220));
  });

  document.addEventListener('keydown', (event) => {
    const container = event.target.closest('[data-predictive-search]');
    if (!container) return;
    if (event.key === 'Escape') { closePredictiveSearch(container); container.querySelector('input[type="search"]')?.focus(); }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const results = container.querySelector('[data-predictive-results]');
    if (!results || results.hidden) return;
    const options = [...results.querySelectorAll('[role="option"]')];
    if (!options.length) return;
    event.preventDefault();
    const current = options.indexOf(document.activeElement);
    const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
    options[next].focus();
  });

  document.addEventListener('shopify:section:load', (event) => initializeModels(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details');
    if (details) details.open = true;
    const slide = event.target.closest('[data-announcement-slide]');
    if (slide) slide.closest('.announcement').querySelectorAll('[data-announcement-slide]').forEach((element) => { element.hidden = element !== slide; });
  });
  initializeModels(document);
})();


